<template>
  <div class="text-center mt-[50px]">
    <div class="cover-container">
      <div class="cover-content">
        <Resource
          :src="character?.cover_url"
          :transform-resource="transformImage"
          class="cover-img"
        />
      </div>
      <InfoText
        :text="$t('ren-wu')"
        class="absolute flex-row-reverse top-[76%] left-1/2 -translate-x-1/2"
        @click="edit({ navKey: ACTOR_NAV.CHARACTER })"
      />
      <div
        v-for="(img, index) in imgs"
        :key="img.url"
        class="img-pointer"
        :style="{
          '--rotateDeg': getRotateDeg(index),
          '--reverseDeg': getRotateDeg(index, true),
        }"
      >
        <SmallImage
          :url="img.url"
          :text="img.text"
          :position="index % 2 === 0 ? 'left' : 'right'"
          @edit="edit(img)"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { capitalize } from 'lodash-es'
import SmallImage from './small-image.vue'
import InfoText from './info-text.vue'
import type {
  Character,
  CharacterCollection,
  CharacterXihe,
} from '~/api/interface/character'
import { ACTOR_NAV, CONFIG_NAV } from '~/constants'
import { createWorkByCharacter } from '~/features/createWork/utils'

interface ImageCompositionProps {
  character?: Character | null
  characterXihe?: CharacterXihe
}

const props = defineProps<ImageCompositionProps>()

const { $t } = useNuxtApp()

function getCollection(
  data: CharacterCollection[] | undefined,
  type: CharacterCollection['type']
) {
  const res = data?.find((item) => item.type === type)
  if (res && type === 'makeup_collection') {
    return res?.is_general?.TRUE ? res : undefined
  }
  return res
}

const _createWork = inject<(source?: SOURCE) => void>('createWork')
const resourceLocation = injectResourceLocation()

function createWork() {
  _createWork?.(resourceLocation?.source)
}

function getRotateDeg(index: number, reverse?: boolean) {
  let deg = '0deg'

  if (imgs.value.length <= 2) {
    if (index === 0) {
      deg = '0deg'
    } else {
      deg = '180deg'
    }

    return reverse ? `-${deg}` : deg
  }

  if (index === 0) {
    deg = '30deg'
  } else if (index === 1) {
    deg = '150deg'
  } else if (index === 2) {
    deg = '0deg'
  } else if (index === 3) {
    deg = '180deg'
  } else if (index === 4) {
    deg = '-30deg'
  } else if (index === 5) {
    deg = '210deg'
  }

  // return reverse ? `-${deg}` : deg

  if (reverse) {
    if (deg.startsWith('-')) {
      return deg.slice(1)
    }
    return `-${deg}`
  } else {
    return deg
  }
}

interface ImgType {
  url: string
  text: string
  navKey: ACTOR_NAV | string
}

const imgs = computed(() => {
  const _imgs: ImgType[] = []

  const hairCollection = getCollection(
    props.characterXihe?.collection_list,
    'hair_collection'
  )

  if (hairCollection?.render_image_oss_pro) {
    _imgs.push({
      url: hairCollection.render_image_oss_pro,
      text: $t('fa-xing'),
      navKey: ACTOR_NAV.HAIR,
    })
  }

  // if (props.character?.cover_url) {
  //   _imgs.push({
  //     url: props.character.cover_url,
  //     text: $t('ren-wu'),
  //   })
  // }

  const collection = getCollection(
    props.characterXihe?.collection_list,
    'collection'
  )

  if (collection?.render_image_oss_pro) {
    // 套装
    _imgs.push({
      url: collection.render_image_oss_pro || '',
      text: $t('tao-zhuang'),
      navKey: ACTOR_NAV.COLLECTION,
    })
  }

  if (props.characterXihe?.accessory_info) {
    props.characterXihe.accessory_info
      .filter((item) =>
        ['Stud', 'Eardrop', 'Earrings', 'Glasses'].includes(
          item.spu__category__name
        )
      )
      .slice(0, 2)
      .forEach((item) => {
        _imgs.push({
          url: item.render_image_oss_pro || '',
          text:
            getEnv('area') === 'CN'
              ? item.spu__category__cn_name
              : capitalize(item.spu__category__cn_name),
          navKey: `${ACTOR_NAV.ACCESSORY}-${item.spu__category__name}-${item.id}`,
        })
      })
  }

  const makeupCollection = getCollection(
    props.characterXihe?.collection_list,
    'makeup_collection'
  )

  if (makeupCollection?.render_image_oss_pro) {
    _imgs.push({
      url: makeupCollection.render_image_oss_pro,
      text: $t('zhuang-rong'),
      navKey: ACTOR_NAV.MAKEUP,
    })
  }

  return _imgs
})

function edit(img: { navKey: string }) {
  createWorkByCharacter(props.character!.id, CONFIG_NAV.ACTOR, img.navKey)
}
</script>
<style lang="scss" scoped>
.cover-container {
  position: relative;
  display: inline-block;
  width: 530px;
  height: 530px;
  background: linear-gradient(
    119.17deg,
    rgba(226, 254, 255, 0.5) 11.89%,
    rgba(221, 219, 255, 0.5) 83.35%
  );
  border-radius: 50%;
}

:global(.dark .cover-container) {
  background: linear-gradient(
    119.17deg,
    rgba(226, 254, 255, 0.2) 11.89%,
    rgba(221, 219, 255, 0.2) 83.35%
  );
}

.cover-content {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 500px;
  height: 500px;
  transform: translate(-50%, -50%);
  background: linear-gradient(
    119.17deg,
    rgb(226, 254, 255) 11.89%,
    rgb(221, 219, 255) 83.35%
  );
  border-radius: 50%;
  overflow: hidden;
}

:global(.dark .cover-content) {
  background: linear-gradient(
    119.17deg,
    rgba(226, 254, 255, 0.8) 11.89%,
    rgba(221, 219, 255, 0.8) 83.35%
  );
}

.img-pointer {
  position: absolute;
  width: calc(50% + 165px);
  top: 50%;
  right: 50%;
  transform: translate(0, -50%) rotate(var(--rotateDeg));
  transform-origin: right center;

  & > div {
    transform: rotate(var(--reverseDeg));
  }
}

.cover-img {
  width: 427px;
}

// .img-left {
//   position: absolute;
//   left:;
// }
</style>
